import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CaretRight, FloppyDisk, PaperPlaneTilt, CheckCircle, XCircle, ArrowUUpLeft,
  RocketLaunch, Archive, Copy, Target, GitBranch, Warning, SealCheck,
} from "@phosphor-icons/react";
import { StatusPill } from "@/components/cms/editorShared";

const STEP_ORDER = ["sandbox", "entwurf", "in_review", "freigegeben", "veroeffentlicht", "archiviert"];

export default function CmsEditorObject() {
  const { library, key } = useParams();
  const nav = useNavigate();
  const [perms, setPerms] = useState(null);
  const [data, setData] = useState(null);
  const [impact, setImpact] = useState(null);
  const [draft, setDraft] = useState("");
  const [changelog, setChangelog] = useState("");
  const [busy, setBusy] = useState(false);
  const [publishGate, setPublishGate] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get(`/v1/cms-core/${library}/${key}`).then((r) => {
      setData(r.data);
      setDraft(JSON.stringify(r.data.current.payload, null, 2));
      setChangelog(r.data.current.changelog || "");
    }).catch(() => setData(false));
    api.get(`/v1/cms-core/${library}/${key}/impact`).then((r) => setImpact(r.data)).catch(() => setImpact(false));
  }, [library, key]);

  useEffect(() => { api.get("/v1/cms-core/permissions").then((r) => setPerms(r.data)).catch(() => setPerms(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const can = (p) => perms?.permissions?.includes(p);
  const cur = data?.current;

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(draft); } catch { toast.error("Payload ist kein gültiges JSON"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.put(`/v1/cms-core/${library}/${key}`, { key, payload: parsed, changelog });
      toast.success(res.mode === "new_draft" ? `Neuer Entwurf v${res.version} erstellt` : `Entwurf gespeichert (${res.changes.length} Änderungen)`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Speichern fehlgeschlagen", { description: d?.errors ? d.errors.join(", ") : formatApiError(d) });
    } finally { setBusy(false); }
  };

  const doTransition = async (action, extra = {}) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/v1/cms-core/${library}/${key}/transition`, { action, ...extra });
      toast.success(`Status: ${res.status}`);
      setPublishGate(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Aktion fehlgeschlagen", { description: typeof d === "object" ? (d.message || JSON.stringify(d)) : formatApiError(d) });
    } finally { setBusy(false); }
  };

  const openPublish = async () => {
    setBusy(true);
    try {
      const { data: gate } = await api.post(`/v1/cms-core/${library}/${key}/publish-preview`);
      setPublishGate(gate);
    } catch (e) { toast.error("Vorschau fehlgeschlagen", { description: formatApiError(e?.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const duplicate = async () => {
    const nk = window.prompt("Neuer Key für die Kopie:", `${key}_kopie`);
    if (!nk) return;
    try {
      await api.post(`/v1/cms-core/${library}/${key}/duplicate`, { key: nk, payload: {} });
      toast.success("Dupliziert"); nav(`/cms-editor/${library}/${nk}`);
    } catch (e) { toast.error("Duplizieren fehlgeschlagen", { description: formatApiError(e?.response?.data?.detail) }); }
  };

  if (data === null) return <div className="space-y-4"><Skeleton className="h-10 w-72 rounded-md" /><Skeleton className="h-96 rounded-md" /></div>;
  if (data === false) return <p className="text-sm text-muted-foreground">Objekt nicht gefunden.</p>;

  const editable = ["sandbox", "entwurf"].includes(cur.status) || cur.status === "veroeffentlicht";

  return (
    <div className="space-y-5" data-testid="cms-object-page">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <button onClick={() => nav("/cms-editor")} className="hover:text-foreground">CMS-Core-Editor</button>
        <CaretRight size={12} /><span>{library}</span><CaretRight size={12} /><span className="font-medium text-foreground">{key}</span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-black tracking-tight" data-testid="cms-object-title">{cur.payload.bezeichnung || cur.payload.name || key}</h1>
            <StatusPill status={cur.status} testid="cms-object-status" />
            <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">{key} · v{cur.version}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Veröffentlichte Version: {data.published_version ? `v${data.published_version}` : "—"} · Copy-on-Write: Bearbeiten veröffentlichter Objekte erzeugt einen neuen Entwurf.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="cms-object-actions">
          {editable && can("edit_draft") && <Button size="sm" onClick={save} disabled={busy} data-testid="cms-act-save"><FloppyDisk size={15} className="mr-1.5" /> Speichern</Button>}
          {cur.status === "entwurf" && can("submit_review") && <Button size="sm" variant="outline" onClick={() => doTransition("submit")} disabled={busy} data-testid="cms-act-submit"><PaperPlaneTilt size={15} className="mr-1.5" /> Einreichen</Button>}
          {cur.status === "in_review" && can("review_approve") && <Button size="sm" onClick={() => doTransition("approve")} disabled={busy} data-testid="cms-act-approve"><CheckCircle size={15} className="mr-1.5" /> Freigeben</Button>}
          {cur.status === "in_review" && can("review_reject") && <Button size="sm" variant="outline" onClick={() => doTransition("reject", { reason: "Ablehnung" })} disabled={busy} data-testid="cms-act-reject"><XCircle size={15} className="mr-1.5" /> Ablehnen</Button>}
          {cur.status === "freigegeben" && can("edit_draft") && <Button size="sm" variant="outline" onClick={() => doTransition("withdraw")} disabled={busy} data-testid="cms-act-withdraw"><ArrowUUpLeft size={15} className="mr-1.5" /> Zurückziehen</Button>}
          {cur.status === "freigegeben" && can("publish") && <Button size="sm" onClick={openPublish} disabled={busy} data-testid="cms-act-publish"><RocketLaunch size={15} className="mr-1.5" /> Veröffentlichen</Button>}
          {can("duplicate") && <Button size="sm" variant="outline" onClick={duplicate} disabled={busy} data-testid="cms-act-duplicate"><Copy size={15} className="mr-1.5" /> Duplizieren</Button>}
          {can("archive") && cur.status !== "archiviert" && <Button size="sm" variant="outline" onClick={() => doTransition("archive")} disabled={busy} data-testid="cms-act-archive"><Archive size={15} className="mr-1.5" /> Archivieren</Button>}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <Card className="rounded-md border">
          <Tabs defaultValue="stammdaten">
            <TabsList className="m-2 flex-wrap">
              <TabsTrigger value="stammdaten" data-testid="tab-stammdaten">Stammdaten</TabsTrigger>
              <TabsTrigger value="referenzen" data-testid="tab-referenzen">Referenzen</TabsTrigger>
              <TabsTrigger value="versionen" data-testid="tab-versionen">Versionen</TabsTrigger>
              <TabsTrigger value="historie" data-testid="tab-historie">Historie</TabsTrigger>
              <TabsTrigger value="diff" data-testid="tab-diff">Vergleich</TabsTrigger>
              <TabsTrigger value="vorschau" data-testid="tab-vorschau">Vorschau</TabsTrigger>
            </TabsList>

            <TabsContent value="stammdaten" className="p-4 pt-0">
              <Label2>Payload (JSON) {!editable && "· schreibgeschützt (archiviert)"}</Label2>
              <Textarea data-testid="cms-payload-editor" value={draft} onChange={(e) => setDraft(e.target.value)} rows={18}
                readOnly={!editable} className="mt-1 font-mono text-xs" />
              <div className="mt-3"><Label2>Änderungsbeschreibung (Changelog)</Label2>
                <Input data-testid="cms-changelog-input" value={changelog} onChange={(e) => setChangelog(e.target.value)} className="mt-1" placeholder="Was wurde geändert und warum?" /></div>
            </TabsContent>

            <TabsContent value="referenzen" className="p-4 pt-0"><ReferencesTab library={library} objKey={key} /></TabsContent>
            <TabsContent value="versionen" className="p-4 pt-0"><VersionsTab library={library} objKey={key} /></TabsContent>
            <TabsContent value="historie" className="p-4 pt-0"><HistoryTab library={library} objKey={key} /></TabsContent>
            <TabsContent value="diff" className="p-4 pt-0"><DiffTab library={library} objKey={key} versions={cur.version} /></TabsContent>
            <TabsContent value="vorschau" className="p-4 pt-0"><PreviewTab library={library} objKey={key} /></TabsContent>
          </Tabs>
        </Card>

        <div className="space-y-4">
          <ImpactPanel impact={impact} />
          <Card className="rounded-md border p-4" data-testid="cms-workflow">
            <div className="mb-3 flex items-center gap-2"><GitBranch size={16} className="text-primary" /><Label2>Workflow-Status</Label2></div>
            <div className="space-y-1.5 text-xs">
              {STEP_ORDER.map((s) => (
                <div key={s} className={`flex items-center gap-2 ${s === cur.status ? "font-bold text-primary" : STEP_ORDER.indexOf(s) < STEP_ORDER.indexOf(cur.status) ? "text-emerald-600" : "text-muted-foreground"}`}>
                  <span className={`h-2 w-2 rounded-full ${s === cur.status ? "bg-primary" : STEP_ORDER.indexOf(s) < STEP_ORDER.indexOf(cur.status) ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
                  {s}{s === cur.status ? " (aktuell)" : ""}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {publishGate && <PublishDialog gate={publishGate} busy={busy} onClose={() => setPublishGate(null)}
        canOverride={can("qg_override")} onConfirm={(extra) => doTransition("publish", extra)} />}
    </div>
  );
}

function ImpactPanel({ impact }) {
  return (
    <Card className="rounded-md border-2 border-primary/30 bg-primary/5 p-4" data-testid="cms-impact-panel">
      <div className="mb-3 flex items-center gap-2"><Target size={16} className="text-primary" /><Label2>Auswirkungen (live)</Label2></div>
      {!impact ? <Skeleton className="h-24 rounded-md" /> : (
        <div className="space-y-1.5 text-sm">
          <Row l="Verwendet in Katalogen" v={impact.verwendet_in_katalogen} testid="impact-catalogs" />
          <div className="pl-1 text-[11px] text-muted-foreground">{(impact.kataloge || []).join(", ") || "—"}</div>
          <Row l="Prüfpunkte" v={impact.referenziert_von_pruefpunkten} testid="impact-checkpoints" />
          <Row l="Business Rules" v={impact.verknuepfte_business_rules} />
          <Row l="Reparaturpakete" v={impact.verknuepfte_reparatur_pakete} />
          <Row l="Mängel" v={impact.verknuepfte_maengel} />
        </div>
      )}
    </Card>
  );
}
const Row = ({ l, v, testid }) => (
  <div className="flex items-center justify-between" data-testid={testid}><span className="text-muted-foreground">{l}</span><span className="font-semibold tabular-nums">{v}</span></div>
);

function ReferencesTab({ library, objKey }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/v1/cms-core/${library}/${objKey}/references`).then((x) => setR(x.data)).catch(() => setR(false)); }, [library, objKey]);
  if (!r) return <Skeleton className="h-40 rounded-md" />;
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="cms-references">
      <div><Label2>Ausgehend</Label2>
        {Object.keys(r.outgoing).length === 0 ? <p className="mt-2 text-xs text-muted-foreground">Keine.</p> :
          Object.entries(r.outgoing).map(([f, ks]) => <div key={f} className="mt-2 text-xs"><span className="text-muted-foreground">{f}:</span> {ks.join(", ")}</div>)}
      </div>
      <div><Label2>Eingehend (referenziert von)</Label2>
        {Object.keys(r.incoming).length === 0 ? <p className="mt-2 text-xs text-muted-foreground">Keine.</p> :
          Object.entries(r.incoming).map(([lib, ks]) => <div key={lib} className="mt-2 text-xs"><span className="text-muted-foreground">{lib}:</span> {ks.join(", ")}</div>)}
      </div>
    </div>
  );
}

function VersionsTab({ library, objKey }) {
  const [vs, setVs] = useState(null);
  useEffect(() => { api.get(`/v1/cms-core/${library}/${objKey}/versions`).then((x) => setVs(x.data)).catch(() => setVs(false)); }, [library, objKey]);
  if (!vs) return <Skeleton className="h-40 rounded-md" />;
  return (
    <table className="w-full text-left text-sm" data-testid="cms-versions">
      <thead className="text-[10px] uppercase tracking-widest text-muted-foreground"><tr className="border-b">
        <th className="py-2">Version</th><th className="py-2">Status</th><th className="py-2">Freigeber</th><th className="py-2">Veröffentlicht</th><th className="py-2">Changelog</th></tr></thead>
      <tbody>{[...vs].reverse().map((v) => (
        <tr key={v.version} className="border-b border-border/60"><td className="py-2 tabular-nums">v{v.version}{v.is_current_published && " ●"}</td>
          <td className="py-2"><StatusPill status={v.status} /></td><td className="py-2 text-xs">{v.approved_by || "—"}</td>
          <td className="py-2 text-xs">{v.published_at ? v.published_at.slice(0, 10) : "—"}</td><td className="py-2 text-xs text-muted-foreground">{v.changelog || "—"}</td></tr>
      ))}</tbody>
    </table>
  );
}

function HistoryTab({ library, objKey }) {
  const [log, setLog] = useState(null);
  useEffect(() => { api.get(`/v1/cms-core/${library}/${objKey}/changelog`).then((x) => setLog(x.data)).catch(() => setLog(false)); }, [library, objKey]);
  if (!log) return <Skeleton className="h-40 rounded-md" />;
  if (log.length === 0) return <p className="text-xs text-muted-foreground">Keine Historie.</p>;
  return (
    <div className="space-y-3" data-testid="cms-history">
      {log.map((e) => (
        <div key={e.id} className="rounded-md border border-border/60 p-3 text-xs">
          <div className="flex items-center justify-between"><span className="font-semibold text-primary">{e.action}</span><span className="text-muted-foreground">{e.actor} · {(e.ts || "").slice(0, 16).replace("T", " ")}</span></div>
          {e.changes?.length > 0 && <ul className="mt-1.5 space-y-0.5 text-muted-foreground">
            {e.changes.map((c, i) => <li key={i}><b className="text-foreground">{c.field}</b>: {JSON.stringify(c.old)} → {JSON.stringify(c.new)}</li>)}</ul>}
          {e.reason && <div className="mt-1 italic text-muted-foreground">Grund: {e.reason}</div>}
        </div>
      ))}
    </div>
  );
}

function DiffTab({ library, objKey, versions }) {
  const [vs, setVs] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => { api.get(`/v1/cms-core/${library}/${objKey}/versions`).then((x) => {
    const nums = x.data.map((v) => v.version); setVs(nums);
    if (nums.length) { setFrom(String(nums[Math.max(0, nums.length - 2)])); setTo(String(nums[nums.length - 1])); }
  }); }, [library, objKey, versions]);
  const run = () => api.get(`/v1/cms-core/${library}/${objKey}/diff`, { params: { from_: Number(from), to: Number(to) } }).then((x) => setRes(x.data));
  useEffect(() => { if (from && to) run(); /* eslint-disable-next-line */ }, [from, to]);
  return (
    <div data-testid="cms-diff">
      <div className="flex items-center gap-2">
        <Select value={from} onValueChange={setFrom}><SelectTrigger className="w-28" data-testid="diff-from"><SelectValue /></SelectTrigger><SelectContent>{vs.map((v) => <SelectItem key={v} value={String(v)}>v{v}</SelectItem>)}</SelectContent></Select>
        <span className="text-muted-foreground">→</span>
        <Select value={to} onValueChange={setTo}><SelectTrigger className="w-28" data-testid="diff-to"><SelectValue /></SelectTrigger><SelectContent>{vs.map((v) => <SelectItem key={v} value={String(v)}>v{v}</SelectItem>)}</SelectContent></Select>
      </div>
      {res && (res.changes.length === 0 ? <p className="mt-4 text-xs text-muted-foreground">Keine Unterschiede.</p> : (
        <table className="mt-4 w-full text-left text-xs"><thead className="text-[10px] uppercase tracking-widest text-muted-foreground"><tr className="border-b"><th className="py-2">Feld</th><th className="py-2">Alt</th><th className="py-2">Neu</th></tr></thead>
          <tbody>{res.changes.map((c, i) => <tr key={i} className="border-b border-border/60"><td className="py-2 font-medium">{c.field}</td><td className="py-2 text-muted-foreground">{JSON.stringify(c.old)}</td><td className="py-2">{JSON.stringify(c.new)}</td></tr>)}</tbody></table>
      ))}
      {res?.breaking?.length > 0 && <div className="mt-3 rounded-md bg-warning/15 p-2 text-xs text-[hsl(38_92%_35%)]"><Warning size={13} className="mr-1 inline" /> {res.breaking.length} Breaking-Hinweis(e): {res.breaking.map((b) => b.detail).join("; ")}</div>}
    </div>
  );
}

function PreviewTab({ library, objKey }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/v1/cms-core/${library}/${objKey}/preview`).then((x) => setP(x.data)).catch(() => setP(false)); }, [library, objKey]);
  if (!p) return <Skeleton className="h-40 rounded-md" />;
  return (
    <div className="space-y-4" data-testid="cms-preview">
      <p className="text-xs text-muted-foreground">Vorschau der betroffenen Kataloge wie vom Monteur gesehen (Entwurf überlagert): {p.affected_catalogs.join(", ") || "—"}</p>
      {p.preview.map((cat) => (
        <div key={cat.catalog_key} className="rounded-md border p-3">
          <div className="font-heading font-bold">{cat.catalog_key} · {cat.name} <span className="text-xs font-normal text-muted-foreground">({cat.total_checkpoints} Prüfpunkte)</span></div>
          <div className="mt-2 space-y-1">
            {cat.gruppen.map((g, i) => (
              <div key={i} className="text-xs">
                <span className="font-semibold">{g.pruefgruppe?.name || g.pruefgruppe?.key}</span>
                <span className="text-muted-foreground"> — {g.checkpoints.map((c) => c.bezeichnung || c.key).join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PublishDialog({ gate, onClose, onConfirm, busy, canOverride }) {
  const [confirmBreaking, setConfirmBreaking] = useState(false);
  const [override, setOverride] = useState(false);
  const blocked = !gate.publish_allowed;
  const qgFail = !gate.quality_gate_pass;
  const allow = gate.referential_integrity_ok && (gate.quality_gate_pass || (canOverride && override)) && (!gate.has_critical_breaking || confirmBreaking);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="cms-publish-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RocketLaunch size={18} className="text-primary" /> Auswirkungsanalyse & Freigabe</DialogTitle>
          <DialogDescription>Was-wäre-wenn vor der Veröffentlichung. Freigabe erst nach bestandenem Quality Gate & Referenzintegrität.</DialogDescription></DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm" data-testid="gate-refint">
            {gate.referential_integrity_ok ? <CheckCircle size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-danger" />}
            Referenzintegrität {gate.referential_integrity_ok ? "ok" : `verletzt: ${(gate.reference_errors || []).join(", ")}`}
          </div>

          <div><Label2>Quality Gate betroffener Kataloge</Label2>
            <div className="mt-1 space-y-1" data-testid="gate-qg">
              {(gate.quality_gate || []).map((q) => (
                <div key={q.catalog} className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm">
                  <span className="flex items-center gap-2"><SealCheck size={14} className="text-primary" /> {q.catalog}</span>
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${q.pass ? "bg-success/15 text-success" : "bg-danger/15 text-danger"}`}>{q.pass ? `PASS ${q.score ?? ""}` : "FAIL"}</span>
                </div>
              ))}
            </div>
          </div>

          {gate.breaking_changes?.length > 0 && (
            <div className="rounded-md bg-warning/10 p-3 text-xs" data-testid="gate-breaking">
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-[hsl(38_92%_35%)]"><Warning size={14} /> Breaking Changes</div>
              {gate.breaking_changes.map((b, i) => <div key={i}>{b.level === "kritisch" ? "🔴" : "🟠"} {b.field}: {b.detail}</div>)}
            </div>
          )}

          {gate.has_critical_breaking && (
            <label className="flex items-center gap-2 text-sm" data-testid="gate-confirm-breaking">
              <input type="checkbox" checked={confirmBreaking} onChange={(e) => setConfirmBreaking(e.target.checked)} /> Kritische Breaking Changes bestätigen
            </label>
          )}
          {qgFail && canOverride && (
            <label className="flex items-center gap-2 text-sm text-danger" data-testid="gate-qg-override">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Quality-Gate-Override (nur Admin, wird auditiert)
            </label>
          )}

          <div className={`rounded-md p-2 text-center text-sm font-semibold ${allow ? "bg-success/15 text-success" : "bg-danger/15 text-danger"}`} data-testid="gate-verdict">
            {allow ? "Veröffentlichung möglich" : "Veröffentlichung blockiert"}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!allow || busy} data-testid="cms-publish-confirm"
            onClick={() => onConfirm({ confirm_breaking: confirmBreaking, qg_override: override })}>
            Jetzt veröffentlichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
